import { createSvgEl } from '@learnforge/demo-render';
import type { Projector } from './curve';

/** The maximum number of ticks rendered per field (density² cap). */
const MAX_TICKS = 400;

/** Maximum per-axis density derived from the cap: floor(sqrt(MAX_TICKS)) = 20. */
const MAX_DENSITY = Math.floor(Math.sqrt(MAX_TICKS));

/** Half-length of each tick in screen pixels. Total tick length = 2 × HALF_LEN. */
const HALF_LEN = 8;

export interface SlopeTicksHandle {
  /**
   * Draw a slope field on the lattice covering `xDomain × yDomain`.
   *
   * For each of the `density × density` lattice points the slope `m = g(x, y)`
   * is computed and a short segment of fixed screen length (`16px`) is drawn,
   * centered on the projected lattice point, at screen angle `atan(m)`.
   *
   * Screen y is inverted relative to math y (SVG y increases downward), so a
   * positive slope tilts visually up-right: the dx component is positive and
   * the dy component is negative in screen space.
   *
   * If `density > 20` the value is clamped to 20 (keeping total ticks ≤ 400).
   * A `console.warn` is emitted when the cap is applied.
   *
   * Calling `setField` again replaces the previous field.
   */
  setField(
    g: (x: number, y: number) => number,
    xDomain: [number, number],
    yDomain: [number, number],
    density: number,
  ): void;

  /** Remove the owning `<g>` from the SVG. */
  destroy(): void;
}

/**
 * Create a slope-field render layer that owns a single `<g>` of `<line>` elements.
 * Append it after the grid and before the figure for correct z-order.
 */
export function createSlopeTicks(svg: SVGSVGElement, projector: Projector): SlopeTicksHandle {
  const group = createSvgEl('g', { class: 'calc-slope-field' });
  svg.appendChild(group);

  return {
    setField(
      g: (x: number, y: number) => number,
      xDomain: [number, number],
      yDomain: [number, number],
      density: number,
    ): void {
      // Clear any previously rendered lines.
      while (group.firstChild) {
        group.removeChild(group.firstChild);
      }

      // Cap density so total ticks stay ≤ MAX_TICKS.
      let d = Math.max(1, Math.round(density));
      if (d > MAX_DENSITY) {
        console.warn(
          `createSlopeTicks: density ${d} exceeds the per-axis cap of ${MAX_DENSITY} ` +
            `(max ${MAX_TICKS} ticks). Clamping to ${MAX_DENSITY}.`,
        );
        d = MAX_DENSITY;
      }

      const [x0, x1] = xDomain;
      const [y0, y1] = yDomain;

      // Step sizes between lattice points. With d points per axis, there are
      // d−1 intervals when d > 1, so the range is divided by (d−1). For d=1
      // we place the single point at the domain midpoint.
      const xStep = d > 1 ? (x1 - x0) / (d - 1) : 0;
      const yStep = d > 1 ? (y1 - y0) / (d - 1) : 0;

      for (let i = 0; i < d; i++) {
        const wx = d > 1 ? x0 + i * xStep : (x0 + x1) / 2;

        for (let j = 0; j < d; j++) {
          const wy = d > 1 ? y0 + j * yStep : (y0 + y1) / 2;

          const m = g(wx, wy);

          // Project the lattice point to screen.
          const center = projector.worldToScreen({ x: wx, y: wy });

          // Compute screen-space direction vector of unit length.
          // angle = atan(m) in standard math coords; in SVG coords y is flipped,
          // so a positive math slope should slant up-right visually:
          //   dx = cos(angle), dy = -sin(angle)  (negate dy to un-invert).
          const angle = Math.atan(m);
          const cosA = Math.cos(angle);
          const sinA = Math.sin(angle);

          const line = createSvgEl('line');
          line.setAttribute('x1', String(center.x - HALF_LEN * cosA));
          line.setAttribute('y1', String(center.y + HALF_LEN * sinA));
          line.setAttribute('x2', String(center.x + HALF_LEN * cosA));
          line.setAttribute('y2', String(center.y - HALF_LEN * sinA));
          group.appendChild(line);
        }
      }
    },

    destroy(): void {
      group.remove();
    },
  };
}
